/**
 * Store settings shell — layout, nav, and section composition.
 * Form logic: ./store-settings/useStoreSettings.js
 * Pure transforms: ./store-settings/storeSettingsForm.js
 */
import React from 'react';
import '../../atomic/inputbox/InputBox.scss';
import './StoreSettings-Page.css';
import { useStoreSettings } from './store-settings/useStoreSettings';
import { StoreProfileSection } from './store-settings/sections/StoreProfileSection';
import { HoursRestrictedSection } from './store-settings/sections/HoursRestrictedSection';
import { MenuAvailabilitySection } from './store-settings/sections/MenuAvailabilitySection';
import { DeliveryAgreementSection } from './store-settings/sections/DeliveryAgreementSection';
import { PickupPromotionSection } from './store-settings/sections/PickupPromotionSection';
import { DeliveryPickupOptions } from './store-settings/sections/DeliveryPickupOptions';
import { SearchKeywordsSection } from './store-settings/sections/SearchKeywordsSection';
import { FilterCategorySection } from './store-settings/sections/FilterCategorySection';
import { CouponsSection } from './store-settings/sections/CouponsSection';

export {
  mapLoadResponseToForm,
  isTabDirty,
  buildTabUpdatePayload,
  buildStoreSettingsPayload,
} from './store-settings/storeSettingsForm';

const StoreSettings = () => {
  const v = useStoreSettings();

  if (!v.allowLayout) {
    return (
      <div className='store-settings-mobile-wrap'>
        <div className='store-settings-mobile-card'>
          <div className='store-settings-mobile-title'>Tablet / Desktop only</div>
          <div className='store-settings-mobile-text'>
            This page is designed for iPad and desktop. Please open it on a larger screen.
          </div>
        </div>
      </div>
    );
  }

  const meta = v.sectionMeta.find((s) => s.id === v.selectedId);

  const renderPanel = () => {
    switch (v.selectedId) {
      case '1':
        return (
          <StoreProfileSection
            meta={meta}
            form={v.form}
            storeImageFile={v.storeImageFile}
            setStoreImageFile={v.setStoreImageFile}
            storeImagePreviewUrl={v.storeImagePreviewUrl}
            setStoreImagePreviewUrl={v.setStoreImagePreviewUrl}
            storeFrontImageFiles={v.storeFrontImageFiles}
            setStoreFrontImageFile={v.setStoreFrontImageFile}
            storeFrontImagePreviewUrls={v.storeFrontImagePreviewUrls}
            setStoreFrontImagePreviewUrl={v.setStoreFrontImagePreviewUrl}
            updateField={v.updateField}
            updateStoreFrontImageUrl={v.updateStoreFrontImageUrl}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '2':
        return (
          <HoursRestrictedSection
            meta={meta}
            form={v.form}
            updateField={v.updateField}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '3':
        return (
          <MenuAvailabilitySection
            meta={meta}
            form={v.form}
            updateField={v.updateField}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '4':
        return (
          <DeliveryAgreementSection
            meta={meta}
            form={v.form}
            updateDeliveryAgreement={v.updateDeliveryAgreement}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '5':
        return (
          <PickupPromotionSection
            meta={meta}
            form={v.form}
            updatePickupPromoField={v.updatePickupPromoField}
            updateFreeItem={v.updateFreeItem}
            addFreeItem={v.addFreeItem}
            removeFreeItem={v.removeFreeItem}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '6':
        return (
          <DeliveryPickupOptions
            meta={meta}
            form={v.form}
            updateField={v.updateField}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '7':
        return (
          <SearchKeywordsSection
            meta={meta}
            form={v.form}
            updateKeyword={v.updateKeyword}
            addKeyword={v.addKeyword}
            removeKeyword={v.removeKeyword}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '8':
        return (
          <FilterCategorySection
            meta={meta}
            form={v.form}
            toggleFoodFilterCategory={v.toggleFoodFilterCategory}
            toggleReligiousFilterCategory={v.toggleReligiousFilterCategory}
            tabFooterProps={v.tabFooterProps}
          />
        );
      case '9':
        return <CouponsSection meta={meta} />;
      default:
        return null;
    }
  };

  return (
    <div className='store-settings-page'>
      <div className='store-settings-shell' style={{ maxWidth: v.layoutMaxWidth }}>
        <aside className='store-settings-toc'>
          <div className='store-settings-toc-header'>Settings</div>
          {v.sectionMeta.map((section) => {
            const { Icon } = section;
            const isActive = v.selectedId === section.id;
            return (
              <div
                key={section.id}
                data-ss-tab
                className={
                  isActive
                    ? 'store-settings-toc-link store-settings-toc-link--active'
                    : 'store-settings-toc-link'
                }
                onClick={() => v.setSelectedId(section.id)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') v.setSelectedId(section.id);
                }}
              >
                <Icon sx={{ fontSize: 15, flexShrink: 0, opacity: isActive ? 1 : 0.7 }} />
                {section.title}
              </div>
            );
          })}
        </aside>

        <main className='store-settings-content'>
          <div className='store-settings-form-shell'>
            {v.loadInFlight && v.baseline == null && (
              <div className='store-settings-load-banner'>Loading store settings…</div>
            )}
            <div className='store-settings-form-panel'>{renderPanel()}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StoreSettings;
